import { useState } from "react";
import axios from "axios";
import { X, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import { BACKEND_URL } from "../config/api";
const API = `${BACKEND_URL}/api`;

/**
 * SuggestPlaceModal — submits a missing-place request to the existing
 * /api/feedback endpoint as an `improvement` category, prefixed with
 * [Missing Place] so admins can filter them on /admin/feedback.
 */
const SuggestPlaceModal = ({ open, onClose, prefilledQuery = "" }) => {
  const [name, setName] = useState(prefilledQuery);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Please enter a place name");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        title: `[Missing Place] ${name.trim()}`,
        description: details.trim() || `User reports "${name.trim()}" is missing from the destination picker.`,
        category: "improvement",
      });
      toast.success("Thanks! We'll review your suggestion.", {
        description: "Track its status on the Feedback page.",
      });
      setName("");
      setDetails("");
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to submit";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="suggest-place-backdrop"
    >
      <div
        className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <MessageSquarePlus className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-charcoal">Suggest a missing place</h3>
              <p className="text-xs text-muted-foreground">We'll review and add it</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-charcoal/5 rounded"
            data-testid="suggest-place-close-btn"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="suggest-name" className="text-sm font-medium">
              Place name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="suggest-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Wakanda, Atlantis, Camp Bestival"
              maxLength={120}
              autoFocus
              required
              data-testid="suggest-place-name-input"
            />
          </div>

          <div>
            <Label htmlFor="suggest-details" className="text-sm font-medium">
              Details (optional)
            </Label>
            <Textarea
              id="suggest-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Country, region, or anything that helps us locate it"
              rows={3}
              maxLength={500}
              data-testid="suggest-place-details-input"
            />
          </div>

          <div className="bg-background rounded-lg p-3 text-xs text-muted-foreground">
            Your suggestion will appear on the public Feedback board where the community can upvote it. Track its status anytime under <span className="font-semibold text-charcoal">Profile → Feedback</span>.
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
              data-testid="suggest-place-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !name.trim()}
              className="flex-1"
              data-testid="suggest-place-submit-btn"
            >
              {submitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SuggestPlaceModal;
